import { memo, useCallback } from 'react'
import { IconTrendingUp } from '@tabler/icons-react'
import Link from 'next/link'

import {
  ALGOLIA_QUERY_SUGGESTIONS_INDEX_NAME,
  getAlgoliaInsightsDataAttributes,
  getCurrentAlgoliaInsightsUserToken,
  sendAlgoliaInsightsClickEvent
} from 'lib/algolia'
import { capitalize } from 'shared/utils/common'
import { PATHS } from 'shared/utils/paths'

import {
  SearchParamsKeys,
  SearchQuerySuggestionItem,
  SearchQuerySuggestionSource
} from '../types'

import SearchQuerySuggestionsSkeleton from './skeletons/SearchQuerySuggestionsSkeleton'

import styles from './SearchQuerySuggestions.module.scss'

interface SearchQuerySuggestionsProps {
  querySuggestions: SearchQuerySuggestionItem[]
  isLoading: boolean
  isInstantResultsActive: boolean
  customerId?: string
}

const SearchQuerySuggestions = ({
  querySuggestions,
  isLoading,
  isInstantResultsActive,
  customerId
}: SearchQuerySuggestionsProps) => {
  // Covers middle-click / ctrl-click "open in new tab", which fires a native `auxclick`
  // that GTM's click-based insights trigger (see data-insights-click-event-trigger) misses.
  const handleAuxClick = useCallback(
    async (e: React.MouseEvent, item: SearchQuerySuggestionItem) => {
      if (e.button !== 1 || !item.queryID || !isInstantResultsActive) return

      const userToken = await getCurrentAlgoliaInsightsUserToken(customerId)

      sendAlgoliaInsightsClickEvent({
        index: ALGOLIA_QUERY_SUGGESTIONS_INDEX_NAME,
        queryID: item.queryID,
        objectID: item.value,
        position: item.sourcePosition,
        userToken
      })
    },
    [customerId, isInstantResultsActive]
  )

  return (
    <div
      className={styles.suggestionsContainer}
      data-insights-index={
        isInstantResultsActive
          ? ALGOLIA_QUERY_SUGGESTIONS_INDEX_NAME
          : undefined
      }
    >
      <h3 className={styles.suggestionsTitle}>
        {isInstantResultsActive ? 'Najczęściej wyszukiwane' : 'Sugerowane'}
      </h3>
      {isLoading ? (
        <SearchQuerySuggestionsSkeleton />
      ) : (
        <ul className={styles.suggestionsList}>
          {querySuggestions.map(item => {
            const label =
              item.source === SearchQuerySuggestionSource.trendingShortNameFacet
                ? capitalize(item.value)
                : item.value

            return (
              <li
                className={styles.suggestionChip}
                key={`${item.source}:${item.value}:${item.sourcePosition}`}
              >
                <Link
                  className={styles.suggestionChipContent}
                  href={`${PATHS.searchResults}?${SearchParamsKeys.searchQuery}=${encodeURIComponent(
                    label
                  )}`}
                  {...(isInstantResultsActive
                    ? {
                        ...getAlgoliaInsightsDataAttributes(
                          item.queryID,
                          item.value,
                          item.sourcePosition
                        ),
                        'data-insights-click-event-trigger': true, // Custom attribute to trigger Algolia insights click event in GTM. Only for instant query suggestions, not for trending queries
                        onAuxClick: (e: React.MouseEvent) =>
                          void handleAuxClick(e, item)
                      }
                    : {})}
                >
                  {!isInstantResultsActive && <IconTrendingUp />}
                  <span>{label}</span>
                </Link>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

export default memo(SearchQuerySuggestions)
